<template>
	<v-select class="style-chooser" label="text" :placeholder="placeholder" :options="options"
		:reduce="(option) => option.value" :value="value" :filterable="false" :loading="loading"
		@search="onSearch" @open="onOpen" @input="onInput">
		<template v-slot:no-options="{ search, searching }">
			<template v-if="searching">
				No results found for
				<em>
					<strong>{{ search }}</strong>
				</em>
			</template>
			<em :style="{ opacity: 0.5 }" v-else>
				Start typing to search
			</em>
		</template>
		<template v-if="hasMore" v-slot:list-footer>
			<li class="pl-3 py-1">
				<button type="button" class="btn btn-sm btn-link p-0" :disabled="loading" @click="loadMore">
					Load more
				</button>
			</li>
		</template>
	</v-select>
</template>

<script>
import _ from 'lodash';
import { getScopeCache } from './asyncPaginatedSelectCache';

// Generic server-paginated + searchable select. `fetchPage(search, page)` must
// resolve to `{ items: [{ value, text }], hasMore }`, capped at 20 items/page
// by the caller. Used in place of building `:options` from a fully preloaded
// in-memory collection (see src/utils/dropDownItemsUtil.js) when the backing
// collection can be large and only one record needs to be picked.
export default {
	name: 'AsyncPaginatedSelect',

	props: {
		value: { type: [String, Object], default: null },
		fetchPage: { type: Function, required: true },
		placeholder: { type: String, default: ' - Please select - ' },
		// Pre-seeds the option list with the currently selected record's
		// { value, text } so its label renders before the user opens the dropdown.
		initialOption: { type: Object, default: null },
		// Identifies the "scope" this select currently fetches within (e.g. a
		// parent company id for a cascading dropdown). Changing it swaps in a
		// per-scope cache of already-loaded pages instead of discarding
		// everything, so flipping back to a previously visited scope doesn't
		// refetch data already in hand.
		cacheKey: { type: [String, Number], default: '' },
		// Stable id identifying this particular dropdown (e.g. "disposal-asset-code").
		// When set, loaded pages/scroll position survive this component being
		// destroyed and recreated (e.g. by vue-router navigating away and back),
		// instead of only surviving cacheKey changes within one mounted instance.
		cacheId: { type: String, default: null },
	},

	data() {
		return {
			options: this.initialOption ? [this.initialOption] : [],
			page: 1,
			search: '',
			loading: false,
			hasMore: false,
			opened: false,
			scopeCache: getScopeCache(this.cacheId),
			// vue-select clears its internal search box after a selection
			// (clearSearchOnSelect) and emits a "search" event for that clear —
			// indistinguishable from a real user search unless we flag it.
			ignoreNextEmptySearch: false,
		};
	},

	created() {
		this.debouncedSearch = _.debounce((search) => this.runSearch(search, true), 300);
		this.restoreScope(this.cacheKey);
	},

	beforeDestroy() {
		// Snapshot the current scope so a future instance sharing this cacheId
		// (e.g. after navigating away and back) can restore it in created().
		this.saveScope(this.cacheKey);
	},

	watch: {
		initialOption(newVal) {
			// A prop change that merely re-describes the value we already have
			// selected (e.g. a caller's computed prop derived from this very
			// component's own v-model), or clears to nothing, isn't a reason to
			// discard already-loaded options/pages — only reseed for a
			// genuinely different record (e.g. a reusable modal now editing a
			// different one).
			if (!newVal || this.describesCurrentValue(newVal)) {
				if (newVal && !this.hasOption(newVal)) {
					this.options = [newVal, ...this.options];
				}
				return;
			}

			this.options = [newVal];
			this.page = 1;
			this.search = '';
			this.hasMore = false;
			this.opened = false;
		},
		// Stash the outgoing scope's loaded state and restore the incoming
		// scope's, so switching between e.g. cascading dropdown parents reuses
		// previously fetched pages instead of refetching every time.
		cacheKey(newKey, oldKey) {
			this.saveScope(oldKey);
			this.restoreScope(newKey);
		},
	},

	methods: {
		saveScope(key) {
			this.scopeCache[key] = {
				options: this.options,
				page: this.page,
				hasMore: this.hasMore,
				opened: this.opened,
				search: this.search,
			};
		},
		restoreScope(key) {
			let cached = this.scopeCache[key];
			if (cached) {
				this.options = cached.options;
				this.page = cached.page;
				this.hasMore = cached.hasMore;
				this.opened = cached.opened;
				this.search = cached.search;
				// The restored page(s) may predate the record we're now showing
				// (e.g. a different mount's initialOption) — make sure its label
				// still renders even if it wasn't part of the cached results.
				if (this.initialOption && !this.hasOption(this.initialOption)) {
					this.options = [this.initialOption, ...this.options];
				}
			} else {
				this.options = this.initialOption ? [this.initialOption] : [];
				this.page = 1;
				this.hasMore = false;
				this.opened = false;
				this.search = '';
			}
		},
		valueKey(val) {
			if (val === null || typeof val === 'undefined') return null;
			return typeof val === 'object' ? val.id : val;
		},
		describesCurrentValue(option) {
			return !!option && this.valueKey(option.value) === this.valueKey(this.value);
		},
		hasOption(option) {
			let key = this.valueKey(option.value);
			return _.some(this.options, (o) => this.valueKey(o.value) === key);
		},
		onOpen() {
			if (!this.opened) {
				this.opened = true;
				this.runSearch(this.search, true);
			}
		},
		onInput(val) {
			// Only a real selection (not a clear) triggers vue-select's
			// clearSearchOnSelect side effect that we need to ignore below.
			if (val !== null && typeof val !== 'undefined') {
				this.ignoreNextEmptySearch = true;
			}
			this.$emit('input', val);
		},
		onSearch(query) {
			if (this.ignoreNextEmptySearch) {
				this.ignoreNextEmptySearch = false;
				if (!query) return;
			}
			this.search = query;
			this.debouncedSearch(query);
		},
		async runSearch(search, reset) {
			this.loading = true;
			try {
				let page = reset ? 1 : this.page;
				let { items, hasMore } = await this.fetchPage(search, page);
				this.options = reset ? items : [...this.options, ...items];
				this.page = page + 1;
				this.hasMore = !!hasMore;
			} finally {
				this.loading = false;
			}
		},
		loadMore() {
			// Guards against redundant fetches: an in-flight request already
			// covers this click, and no "hasMore" means every page is loaded.
			if (this.loading || !this.hasMore) return;
			this.runSearch(this.search, false);
		},
	},
};
</script>
